import { useEffect, useRef, useState } from 'react';
import { resolveImageUrl } from '../lib/utils';

export const parseYouTubeId = (url = '') => {
  const m = String(url).match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/
  );
  return m ? m[1] : '';
};

export const slideMediaType = (slide = {}) => {
  if (slide.media_type) return slide.media_type;
  if (slide.video_url) return 'video';
  if (slide.youtube_url) return 'youtube';
  return 'image';
};

const youTubeSrc = (slide) => {
  const id = parseYouTubeId(slide.youtube_url);
  if (!id) return '';
  const p = new URLSearchParams({
    autoplay: '1', mute: '1', controls: '0', loop: '1', playlist: id,
    modestbranding: '1', rel: '0', playsinline: '1', iv_load_policy: '3',
    disablekb: '1', fs: '0',
  });
  if (Number(slide.yt_start) > 0) p.set('start', String(Math.floor(slide.yt_start)));
  if (Number(slide.yt_end) > 0) p.set('end', String(Math.floor(slide.yt_end)));
  return `https://www.youtube-nocookie.com/embed/${id}?${p.toString()}`;
};

/* Renders one hero slide's media: still, self-hosted loop, or YouTube segment.
   Falls back to the poster on small screens, for reduced motion, and off-screen. */
export default function HeroMedia({ slide, active, inView = true }) {
  const type = slideMediaType(slide);
  const videoRef = useRef(null);
  const [lightMode, setLightMode] = useState(true);
  const poster = resolveImageUrl(slide.poster_url || slide.url);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px), (prefers-reduced-motion: reduce)');
    const apply = () => setLightMode(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    /* source children can mount after the element — force a fresh selection */
    v.load();
  }, [slide.video_url, slide.video_url_webm, lightMode]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (active && inView) {
      const start = Number(slide.yt_start) || 0;
      if (start > 0 && v.currentTime < start) v.currentTime = start;
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [active, inView, slide.yt_start]);

  const onTimeUpdate = () => {
    const v = videoRef.current;
    const end = Number(slide.yt_end) || 0;
    const start = Number(slide.yt_start) || 0;
    if (v && end > 0 && v.currentTime >= end) v.currentTime = start;
  };

  if (type === 'video' && slide.video_url && !lightMode) {
    const webm = slide.video_url_webm || (slide.video_url.endsWith('.mp4') ? slide.video_url.replace(/\.mp4$/, '.webm') : '');
    const canMp4 = document.createElement('video').canPlayType('video/mp4; codecs="avc1.42E01E"') !== '';
    const src = canMp4 || !webm ? slide.video_url : webm;
    return (
      <video
        ref={videoRef}
        data-testid="hero-video"
        src={resolveImageUrl(src)}
        poster={poster}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        onLoadedMetadata={() => { if (active && inView) videoRef.current?.play().catch(() => {}); }}
        onTimeUpdate={onTimeUpdate}
        className="absolute inset-0 w-full h-full object-cover"
      />
    );
  }

  if (type === 'youtube' && parseYouTubeId(slide.youtube_url) && !lightMode) {
    return (
      <div className="absolute inset-0 overflow-hidden" data-testid="hero-youtube">
        {poster && <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        {active && inView && (
          <iframe
            title={slide.title || 'Axicor'}
            src={youTubeSrc(slide)}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen={false}
            tabIndex={-1}
            /* 16:9 blown up so the player chrome sits outside the frame */
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[178vh] h-[100vh] min-w-full min-h-full pointer-events-none border-0"
          />
        )}
      </div>
    );
  }

  return (
    <img
      src={poster}
      alt={slide.title || ''}
      loading={active ? 'eager' : 'lazy'}
      decoding={active ? 'sync' : 'async'}
      fetchPriority={active ? 'high' : 'low'}
      className="absolute inset-0 w-full h-full object-cover"
    />
  );
}
